import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, PenLine, Plus } from "lucide-react";
import axios from "axios";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, fileUrl } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/signatories`;
const ROLE_AR = { owner: "مالك", co_owner: "شريك", manager: "مدير", hr_manager: "الموارد البشرية", finance: "المالية", supervisor: "مشرف", operations_manager: "التشغيل", reviewer: "مراجع", user: "مستخدم" };

const Row = ({ s, onChange, onDelete }) => {
  const [title, setTitle] = useState(s.title || "");
  const [dname, setDname] = useState(s.display_name || "");
  const [busy, setBusy] = useState(false);
  const save = async (patch) => {
    setBusy(true);
    try { onChange((await axios.post(API, { user_id: s.user_id, title, display_name: dname, signature_id: s.signature_id || "", ...patch })).data); toast.success("تم الحفظ"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const upload = async (f) => { if (!f) return; setBusy(true); try { const r = await api.uploadFile(f); await save({ signature_id: r.attachment_id || r.id }); } catch { toast.error("تعذّر رفع التوقيع"); setBusy(false); } };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`signatory-${s.user_id}`}>
      <div className="flex h-14 w-28 items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50">
        {s.signature_id ? <img src={fileUrl(s.signature_id)} alt="" className="h-full w-full object-contain" /> : <PenLine className="h-5 w-5 text-slate-300" />}
      </div>
      <div className="min-w-[160px] flex-1">
        <p className="text-sm font-semibold text-slate-800">{s.name} <span className="text-xs font-normal text-slate-400">· {ROLE_AR[s.role] || s.role}</span></p>
        <div className="mt-1 grid gap-1.5 sm:grid-cols-2">
          <Input value={dname} onChange={(e) => setDname(e.target.value)} onBlur={() => dname !== (s.display_name || "") && save({})} placeholder="الاسم اللي يظهر في المستند"
            className="h-8 text-xs" data-testid={`signatory-name-${s.user_id}`} />
          <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => title !== (s.title || "") && save({})} placeholder="المسمّى الوظيفي (مثال: مدير الموارد البشرية)"
            className="h-8 text-xs" data-testid={`signatory-title-${s.user_id}`} />
        </div>
      </div>
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs hover:bg-slate-50" data-testid={`signatory-upload-${s.user_id}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {s.signature_id ? "تغيير التوقيع" : "رفع التوقيع"}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      <button type="button" onClick={onDelete} className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50" data-testid={`signatory-delete-${s.user_id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
};

const DEPTS = [["hr", "الموارد البشرية", "القرارات والخطابات"], ["finance", "المالية", "السلف وكشوف الحساب والفواتير"], ["ops", "التشغيل", "سندات العُهد والمركبات وعقود التأجير"]];

const DeptRow = ({ dept, label, hint, s, onChange, roleId }) => {
  const [f, setF] = useState({ display_name: s?.display_name || "", title: s?.title || "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ display_name: s?.display_name || "", title: s?.title || "" }); }, [s?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (patch = {}) => {
    setBusy(true);
    try {
      const body = { ...f, signature_id: s?.signature_id || "", ...patch };
      onChange((roleId ? await axios.post(`${API}/role`, { role_id: roleId, role_name: label, ...body }) : await axios.post(`${API}/dept`, { dept, ...body })).data);
      toast.success("تم الحفظ");
    }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const upload = async (file) => { if (!file) return; setBusy(true); try { const r = await api.uploadFile(file); await save({ signature_id: r.attachment_id || r.id }); } catch { toast.error("تعذّر رفع التوقيع"); setBusy(false); } };
  const blur = (k) => f[k] !== (s?.[k] || "") && save();
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`dept-signatory-${dept || roleId}`}>
      <div className="flex h-14 w-28 items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50">
        {s?.signature_id ? <img src={fileUrl(s.signature_id)} alt="" className="h-full w-full object-contain" /> : <PenLine className="h-5 w-5 text-slate-300" />}
      </div>
      <div className="min-w-[180px] flex-1">
        <p className="text-sm font-semibold text-slate-800">{label} <span className="text-xs font-normal text-slate-400">· {hint}</span></p>
        <div className="mt-1 grid gap-1.5 sm:grid-cols-2">
          <Input value={f.display_name} onChange={(e) => setF((p) => ({ ...p, display_name: e.target.value }))} onBlur={() => blur("display_name")} placeholder="اسم المسؤول" className="h-8 text-xs" data-testid={`dept-signatory-name-${dept || roleId}`} />
          <Input value={f.title} onChange={(e) => setF((p) => ({ ...p, title: e.target.value }))} onBlur={() => blur("title")} placeholder={`المسمّى (مثال: مدير ${label})`} className="h-8 text-xs" data-testid={`dept-signatory-title-${dept || roleId}`} />
        </div>
      </div>
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs hover:bg-slate-50" data-testid={`dept-signatory-upload-${dept || roleId}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {s?.signature_id ? "تغيير التوقيع" : "رفع التوقيع"}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {s?.signature_id && <button type="button" onClick={() => save({ signature_id: "" })} className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50" title="إزالة التوقيع" data-testid={`dept-signatory-clear-${dept || roleId}`}><Trash2 className="h-4 w-4" /></button>}
    </div>
  );
};

export const SignatorySelect = ({ value, onChange, onPick, testid = "doc-signatory" }) => {
  const [sigs, setSigs] = useState([]);
  const [any, setAny] = useState(false);
  const [mine, setMine] = useState("");
  useEffect(() => { axios.get(API).then((r) => { const it = (r.data.items || []).filter((x) => x.signature_id); setMine(r.data.mine || ""); setAny(it.length > 0); setSigs([...it.filter((x) => x.role_id), ...it.filter((x) => x.dept), ...it.filter((x) => !x.dept && !x.role_id)]); }).catch(() => {}); }, []);
  useEffect(() => {
    if (!onPick) return;
    const s = value === "general" ? null : sigs.find((x) => x.id === (value || mine));
    onPick(s ? { signer_name: s.display_name || s.name || "", signer_title: s.title || (s.role_id || s.dept ? s.name : "") || "", signer_signature_id: s.signature_id } : {});
  }, [value, sigs, mine]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!any) return null;
  const deptAr = Object.fromEntries(DEPTS.map(([k, l]) => [k, l]));
  return (
    <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
      <span>توقيع المسؤول<span className="block text-[11px] text-slate-400">يظهر في الطباعة بعد الحفظ</span></span>
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-[60%] min-w-0 rounded-md border border-slate-200 bg-white px-2 py-1 text-sm">
        <option value="">تلقائي — توقيع دور المنشئ</option>
        <option value="general">التوقيع العام للشركة</option>
        {sigs.map((x) => <option key={x.id} value={x.id}>{x.role_id ? `${x.name || "دور"} — ` : x.dept ? `${deptAr[x.dept] || x.dept} — ` : ""}{x.display_name || (x.role_id ? "" : x.name) || "بدون اسم"}{x.title ? ` — ${x.title}` : ""}</option>)}
      </select>
    </label>
  );
};
export const SIGN_NOTE = "التوقيع المختار يظهر في الطباعة والـ PDF بعد حفظ المستند.";

export const SignatoriesCard = () => {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [pick, setPick] = useState("");
  const [roles, setRoles] = useState([]);
  useEffect(() => {
    axios.get(API).then((r) => setItems(r.data.items)).catch(() => {});
    api.listUsers().then((u) => setUsers(Array.isArray(u) ? u : u.items || [])).catch(() => {});
    api.orgStructure().then((d) => setRoles((d.roles || []).filter((r) => r.id !== "user"))).catch(() => {});
  }, []);
  const avail = users.filter((u) => !items.some((s) => s.user_id === u.user_id));
  const people = items.filter((s) => !s.dept && !s.role_id);
  const legacyDepts = DEPTS.filter(([k]) => items.some((x) => x.dept === k && x.signature_id));
  const setOne = (n) => setItems((x) => (x.some((y) => y.id === n.id) ? x.map((y) => (y.id === n.id ? n : y)) : [...x, n]));
  const add = async () => {
    if (!pick) return;
    try { const r = await axios.post(API, { user_id: pick, title: "", signature_id: "" }); setItems((x) => [...x, r.data]); setPick(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإضافة"); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4" data-testid="signatories-card">
      <div>
        <p className="flex items-center gap-2 font-semibold text-slate-800"><PenLine className="h-4 w-4 text-indigo-600" /> تواقيع المستخدمين حسب الصلاحيات</p>
        <p className="text-xs text-slate-500">لكل دور في قائمة الصلاحيات توقيعه. أي مستند يُطبع تلقائياً بتوقيع دور من أنشأه، وإن لم يكن للدور توقيع يُستخدم التوقيع العام. المالك يستطيع اختيار أي توقيع، وغيره يرى توقيع دوره فقط.</p>
      </div>
      {roles.map((r) => <DeptRow key={r.id} roleId={r.id} label={r.name} hint={r.system ? "دور أساسي" : "دور مخصص"} s={items.find((x) => x.role_id === r.id)} onChange={setOne} />)}
      {legacyDepts.length > 0 && <p className="pt-2 text-xs font-semibold text-slate-600">تواقيع الإدارات السابقة</p>}
      {legacyDepts.map(([k, label, hint]) => <DeptRow key={k} dept={k} label={label} hint={hint} s={items.find((x) => x.dept === k)} onChange={setOne} />)}
      <p className="pt-2 text-xs font-semibold text-slate-600">تواقيع إضافية لأشخاص (اختيارية — تختارها يدوياً من محرر القرار أو الخطاب)</p>
      {people.map((s) => (
        <Row key={s.user_id} s={s} onChange={(n) => setItems((x) => x.map((y) => (y.user_id === n.user_id ? n : y)))}
          onDelete={async () => {
            if (!window.confirm(`حذف توقيع ${s.name}؟`)) return;
            try { await axios.delete(`${API}/${s.id}`); setItems((x) => x.filter((y) => y.id !== s.id)); toast.success("تم الحذف"); }
            catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
          }} />
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={pick} onValueChange={setPick}>
          <SelectTrigger className="h-9 w-64 bg-white" data-testid="signatory-user-select"><SelectValue placeholder="اختر مستخدم لإضافة توقيعه" /></SelectTrigger>
          <SelectContent>{avail.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email} · {ROLE_AR[u.role] || u.role}</SelectItem>)}</SelectContent>
        </Select>
        <Button type="button" size="sm" onClick={add} disabled={!pick} className="gap-1" data-testid="signatory-add"><Plus className="h-4 w-4" /> إضافة</Button>
      </div>
    </div>
  );
};
